import { forwardRef, useImperativeHandle, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProgressIndicator, type ProgressIndicatorStep } from '@/components/design-system/primitives/index.js';
import { Button } from '../design-system/primitives/Button/Button.js';
import { Modal } from '../design-system/primitives/Modal/Modal.js';
import { QuestionSoftTimer } from '../interview/QuestionSoftTimer.js';
import { SkipLanguageCheckModal } from '../interview/SkipLanguageCheckModal.js';
import { useSimulationStore } from '../../store/simulationStore.js';
import { GatorMark } from './GatorMark.js';

export type SessionHeaderProps = {
  currentPhase: string;
  phases: Array<{ id: string; label: string; state: 'completed' | 'active' | 'upcoming' }>;
  elapsedSeconds: number;
  onEndStage: () => void;
  simulationConfig: {
    role: string;
    interviewType: string;
    primaryLanguage: string;
    assessmentLanguage?: string | null;
  } | null;
  controlsEnabled?: boolean;
  phaseLabel?: string;
  endStageReady?: boolean;
  progress?: { current: number; total: number } | null;
  durationLabel?: string | null;
  endActionLabel?: string;
  questionCounterLabel?: string | null;
  onExitInterview: () => void;
  /** Completes/skips stage without native confirm — use after in-app modal confirmation. */
  onEndStageConfirmed?: () => void;
  /** Called after the user confirms skipping the language check via the modal. */
  onSkipLanguageCheckConfirmed?: () => void | Promise<void>;
  children?: ReactNode;
};

const DANGER_ACTION_CLASS =
  'inline-flex h-10 shrink-0 items-center justify-center rounded-lg border border-ig-danger bg-transparent px-4 font-sans text-sm font-medium text-ig-danger transition hover:bg-ig-danger/10 md:h-11 md:px-6';

const LANGUAGE_CHECK_ROUTE_RE = /\/interview\/[^/]+\/language-check$/;

function useInterviewSessionChrome(props: SessionHeaderProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {
    currentPhase,
    phases,
    controlsEnabled = true,
    endActionLabel,
    progress,
  } = props;

  const isLanguageCheckRoute = LANGUAGE_CHECK_ROUTE_RE.test(pathname);
  const activeLabel = (currentPhase ?? t('interview.header.defaultPhase', { defaultValue: 'Interview' })).trim();

  const progressSteps: ProgressIndicatorStep[] = useMemo(() => {
    const stagePhases = phases.filter((phase) => phase.id !== 'language-check').slice(0, 3);
    const languagePhase = phases.find((phase) => phase.id === 'language-check');
    const fallbackLabels = [
      t('interview.feedback.stageLabels.1', { defaultValue: 'Screening' }),
      t('interview.feedback.stageLabels.2', { defaultValue: 'Technical' }),
      t('interview.feedback.stageLabels.3', { defaultValue: 'Pressure' }),
    ];

    if (stagePhases.length > 0) {
      const stagePillsMapped = stagePhases.map((phase, idx) => ({
        id: phase.id || `stage-${idx + 1}`,
        label: phase.label || fallbackLabels[idx] || `Stage ${idx + 1}`,
        state: phase.state,
      }));
      return languagePhase
        ? [
            {
              id: languagePhase.id,
              label: languagePhase.label,
              state: languagePhase.state,
            },
            ...stagePillsMapped,
          ]
        : stagePillsMapped;
    }

    const total = Math.max(1, Math.min(3, progress?.total ?? 3));
    const current = Math.max(1, Math.min(total, progress?.current ?? 1));
    return Array.from({ length: total }, (_, idx) => ({
      id: `stage-${idx + 1}`,
      label: fallbackLabels[idx] || `Stage ${idx + 1}`,
      state: idx + 1 < current ? 'completed' : idx + 1 === current ? 'active' : 'upcoming',
    }));
  }, [phases, progress?.current, progress?.total, t]);

  const primaryActionLabel = isLanguageCheckRoute
    ? t('interview.header.skipLanguageCheck', { defaultValue: 'Skip language check' })
    : (endActionLabel ?? t('interview.header.endStage', { defaultValue: 'End Stage' }));

  return {
    t,
    activeLabel,
    progressSteps,
    primaryActionLabel,
    controlsEnabled,
    isLanguageCheckRoute,
  };
}

export type InterviewSessionChromeHandle = {
  requestEndStageConfirm: () => void;
};

export const InterviewSessionChrome = forwardRef<InterviewSessionChromeHandle, SessionHeaderProps>(
  function InterviewSessionChrome({ children, ...props }, ref) {
  const { t, activeLabel, progressSteps, primaryActionLabel, controlsEnabled, isLanguageCheckRoute } =
    useInterviewSessionChrome(props);
  const activeQuestionTimer = useSimulationStore((s) => s.activeQuestionTimer);
  const [endStageModalOpen, setEndStageModalOpen] = useState(false);
  const [skipLcModalOpen, setSkipLcModalOpen] = useState(false);
  const [skipLcPending, setSkipLcPending] = useState(false);

  useImperativeHandle(ref, () => ({
    requestEndStageConfirm: () => {
      if (isLanguageCheckRoute) {
        setSkipLcModalOpen(true);
      } else {
        setEndStageModalOpen(true);
      }
    },
  }), [isLanguageCheckRoute]);

  const handlePrimaryAction = () => {
    if (isLanguageCheckRoute) {
      setSkipLcModalOpen(true);
      return;
    }
    setEndStageModalOpen(true);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="sticky top-0 z-30 flex w-full shrink-0 flex-col gap-3 border-b border-ig-border bg-ig-bg-primary px-4 py-3 md:h-16 md:flex-row md:items-center md:justify-between md:gap-4 md:px-8 md:py-0">
        <div className="flex min-w-0 items-center justify-between gap-3 md:shrink-0">
          <div className="flex min-w-0 items-center gap-3">
            <GatorMark className="h-8 w-8 shrink-0 rounded-md" alt="" />
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ig-text-tertiary">
              {t('interview.header.interviewLabel', { defaultValue: 'INTERVIEW' })}
            </span>
          </div>
          {props.questionCounterLabel ? (
            <span className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-ig-text-tertiary md:hidden">
              {props.questionCounterLabel}
            </span>
          ) : null}
        </div>
        <div className="flex w-full justify-center md:min-w-0 md:flex-1">
          <ProgressIndicator steps={progressSteps} showLabels={false} className="md:hidden" />
          <ProgressIndicator steps={progressSteps} className="hidden md:flex" />
        </div>
        {props.questionCounterLabel ? (
          <span className="hidden shrink-0 truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-ig-text-tertiary md:inline">
            {props.questionCounterLabel}
          </span>
        ) : null}
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>

      <footer className="sticky bottom-0 z-30 shrink-0 border-t border-ig-border bg-ig-bg-primary px-4 py-3 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <span className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-ig-text-tertiary">
            {activeLabel}
          </span>

          {activeQuestionTimer ? (
            <QuestionSoftTimer
              secondsLeft={activeQuestionTimer.secondsLeft}
              overtime={activeQuestionTimer.overtime}
              estimatedSeconds={activeQuestionTimer.estimatedSeconds}
            />
          ) : null}

          <div className="flex items-center gap-3">
            {controlsEnabled ? (
              <button type="button" className={DANGER_ACTION_CLASS} onClick={handlePrimaryAction}>
                {primaryActionLabel}
              </button>
            ) : (
              <Button type="button" variant="primary" size="md" onClick={props.onExitInterview}>
                {t('interview.header.backToDashboard', { defaultValue: 'Back to Dashboard' })}
              </Button>
            )}
          </div>
        </div>
      </footer>

      <Modal
        open={endStageModalOpen}
        onOpenChange={setEndStageModalOpen}
        title={t('interview.header.endStageTitle', { defaultValue: 'End this stage?' })}
        description={t('interview.header.endStageDescription', {
          defaultValue:
            'This stage will be marked as complete and you will see feedback before continuing. If you have not answered anything, it will be marked as skipped and no feedback will be generated.',
        })}
        size="sm"
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={() => setEndStageModalOpen(false)}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="button"
              variant="danger"
              className="w-full sm:w-auto"
              onClick={() => {
                setEndStageModalOpen(false);
                void (props.onEndStageConfirmed ?? props.onEndStage)();
              }}
            >
              {t('interview.header.confirmEndStage', { defaultValue: 'Yes, end stage' })}
            </Button>
          </div>
        }
      >
        <p className="text-body-14 text-ig-text-secondary">
          {t('interview.header.endStageHelpText', {
            defaultValue: 'You can still continue the interview after reviewing feedback.',
          })}
        </p>
      </Modal>

      <SkipLanguageCheckModal
        open={skipLcModalOpen}
        pending={skipLcPending}
        onCancel={() => setSkipLcModalOpen(false)}
        onConfirm={async () => {
          if (!props.onSkipLanguageCheckConfirmed) {
            setSkipLcModalOpen(false);
            return;
          }
          setSkipLcPending(true);
          try {
            await props.onSkipLanguageCheckConfirmed();
          } finally {
            setSkipLcPending(false);
            setSkipLcModalOpen(false);
          }
        }}
      />
    </div>
  );
});

/** @deprecated Use InterviewSessionChrome — kept for import compatibility */
export function SessionHeader(props: SessionHeaderProps) {
  return <InterviewSessionChrome {...props} />;
}
